import { useCallback, useRef, useState } from 'react';

/**
 * Pending state for a whole multi-request batch, with a re-entry guard.
 *
 * A TanStack mutation's `isPending` tracks its observer, which follows the *latest*
 * call — so for a batch that fans out (`Promise.allSettled` over N users) or iterates
 * (a chunked bulk approve), it goes false while earlier requests are still in flight.
 * Anything gated on it re-enables mid-batch, and a second batch starts underneath the
 * first: racing writes, and two contradictory toasts about the same rows.
 *
 * This is the third time that shape has appeared on this page — the chunked bulk
 * approve, then the same flag reused for the assignment fan-out — so it is a mechanism
 * rather than a third hand-rolled boolean.
 *
 * Two pieces of state on purpose:
 *
 *  - `pending` drives the UI, and must be state so React re-renders the disabled and
 *    inert affordances.
 *  - the ref is the actual guard. State updates are asynchronous, so two activations in
 *    the same tick — a double-click, or Enter arriving on a control that has not yet
 *    re-rendered as disabled — both read the old `false`. A ref is written synchronously
 *    and closes that window, which disabling alone cannot.
 *
 * `run` resolves to `undefined` when it refuses, so a caller can tell "the batch ran and
 * returned nothing" from "the batch was declined because one was already going".
 */
export function useBatchRun(): {
  pending: boolean;
  run: <T>(work: () => Promise<T>) => Promise<T | undefined>;
} {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async <T>(work: () => Promise<T>): Promise<T | undefined> => {
    if (inFlight.current) return undefined;
    inFlight.current = true;
    setPending(true);
    try {
      return await work();
    } finally {
      // `finally`, so a throw cannot strand every batch control disabled for the rest of
      // the session.
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { pending, run };
}

/**
 * The same guard, per key, for actions that act on one row at a time.
 *
 * `useBatchRun` answers "is a batch running"; this answers "is *this row* already
 * mid-request". Both exist because `disabled` cannot stop a same-tick double
 * activation — two clicks, or an Enter arriving on a button that has not re-rendered
 * yet, both read the old state — so the ref is the guard and the state is only what the
 * UI reads.
 *
 * This is the fourth place the shape appeared on the approvals page: the chunked bulk
 * approve, the assignment fan-out, the triage row buttons, and the drawer. The first
 * three were fixed one at a time; the drawer is where writing a fourth copy stopped
 * being defensible.
 */
export function useKeyedRun(): {
  /** Keys with a request in flight, for disabling their own controls. */
  pending: ReadonlySet<string>;
  /** Runs `work` unless `key` is already in flight; resolves to `undefined` if declined. */
  run: <T>(key: string, work: () => Promise<T>) => Promise<T | undefined>;
  /** True without dispatching — for callers that guard before building a request. */
  isPending: (key: string) => boolean;
} {
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const inFlight = useRef<Set<string>>(new Set());

  const sync = useCallback((): void => setPending(new Set(inFlight.current)), []);

  const run = useCallback(
    async <T>(key: string, work: () => Promise<T>): Promise<T | undefined> => {
      if (inFlight.current.has(key)) return undefined;
      inFlight.current.add(key);
      sync();
      try {
        return await work();
      } finally {
        inFlight.current.delete(key);
        sync();
      }
    },
    [sync],
  );

  const isPending = useCallback((key: string): boolean => inFlight.current.has(key), []);

  return { pending, run, isPending };
}
